import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions, logAudit } from "../contexts/PermissionContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  AlertTriangle,
  Barcode,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MapPin,
  Tag,
  FileText,
  X,
  FileSpreadsheet,
  Camera,
  Image,
  Upload,
  Calendar,
  ScanLine,
  Truck,
  Check,
  History,
  Printer,
  ClipboardList,
} from "lucide-react";
import { ProductBarcode } from "../components/BarcodeDisplay";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { exportInventoryToExcel } from "../utils/excelExport";

const API = `${window.location.origin}/api`;

// Labels للحقول بالعربي والإنجليزي
const fieldLabels = {
  ar: {
    code: "الكود",
    name: "اسم الصنف",
    purchase_price: "سعر الشراء",
    sale_price: "سعر البيع",
    quantity: "الكمية",
    category: "الفئة",
    min_stock: "الحد الأدنى",
    unit: "الوحدة",
    barcode: "الباركود",
    supplier_name: "اسم المورد",
    notes: "ملاحظات",
    expiry_date: "تاريخ الانتهاء",
    location: "موقع التخزين",
    brand: "الماركة",
    weight: "الوزن/الحجم",
    variant: "النوع/اللون",
    cost_price: "سعر التكلفة",
    profit_margin: "هامش الربح %",
    basicInfo: "معلومات أساسية",
    priceInfo: "الأسعار والكميات",
    additionalInfo: "معلومات إضافية",
    autoGenerate: "توليد تلقائي",
    codeWillBeGenerated: "سيتم توليد الكود تلقائياً",
    units: {
      piece: "قطعة",
      kg: "كيلوغرام",
      gram: "غرام",
      liter: "لتر",
      meter: "متر",
      box: "صندوق",
      pack: "علبة",
    },
  },
  en: {
    code: "Code",
    name: "Product Name",
    purchase_price: "Purchase Price",
    sale_price: "Sale Price",
    quantity: "Quantity",
    category: "Category",
    min_stock: "Min Stock",
    unit: "Unit",
    barcode: "Barcode",
    supplier_name: "Supplier Name",
    notes: "Notes",
    expiry_date: "Expiry Date",
    location: "Storage Location",
    brand: "Brand",
    weight: "Weight/Size",
    variant: "Variant/Color",
    cost_price: "Cost Price",
    profit_margin: "Profit Margin %",
    basicInfo: "Basic Info",
    priceInfo: "Prices & Quantities",
    additionalInfo: "Additional Info",
    autoGenerate: "Auto Generate",
    codeWillBeGenerated: "Code will be auto-generated",
    units: {
      piece: "Piece",
      kg: "Kilogram",
      gram: "Gram",
      liter: "Liter",
      meter: "Meter",
      box: "Box",
      pack: "Pack",
    },
  },
};

const initialFormData = {
  code: "",
  name: "",
  purchase_price: "",
  sale_price: "",
  quantity: "",
  category: "",
  min_stock: "5",
  unit: "",
  barcode: "",
  supplier_name: "",
  notes: "",
  location: "",
  brand: "",
  weight: "",
  variant: "",
  cost_price: "",
  large_unit: "",
  small_unit: "",
  units_per_package: "",
  profit_margin: "",
  expiry_date: "",
};

export default function InventoryPage() {
  const { } = useAuth();
  const { canViewPurchasePrice } = usePermissions();
  const language = "ar";
  const { t } = useTranslation(language);
  const labels = fieldLabels[language] || fieldLabels.ar;
  
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [autoGenerateCode, setAutoGenerateCode] = useState(true);
  const [nextCode, setNextCode] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [activeTab, setActiveTab] = useState("basic");
  const [isScannerOpen, setIsScannerOpen] = useState(false); // ماسح الباركود للمخزون
  const [productImage, setProductImage] = useState(null); // صورة المنتج
  const [imagePreview, setImagePreview] = useState(null); // معاينة الصورة
  const [uploadingImage, setUploadingImage] = useState(false); // حالة رفع الصورة
  
  // حالات استلام البضاعة
  const [isReceiveMode, setIsReceiveMode] = useState(false); // وضع استلام البضاعة
  const [receiveProduct, setReceiveProduct] = useState(null); // المنتج المراد استلامه
  const [receiveQuantity, setReceiveQuantity] = useState(""); // الكمية المستلمة
  const [receiveScannerOpen, setReceiveScannerOpen] = useState(false); // ماسح استلام البضاعة
  const [receiveHistory, setReceiveHistory] = useState([]); // تاريخ الاستلام في هذه الجلسة
  const [scannerMode, setScannerMode] = useState(false); // وضع المسح المتواصل
  const [lastScanTime, setLastScanTime] = useState(0);
  const [showInventoryReport, setShowInventoryReport] = useState(false); // تقرير الجرد

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchNextCode = async () => {
    try {
      const response = await axios.get(`${API}/products/next-code`);
      setNextCode(response.data.code);
      if (autoGenerateCode && !editingProduct) {
        setFormData(prev => ({ 
          ...prev, 
          code: response.data.code,
          barcode: response.data.barcode || ""
        }));
      }
    } catch (error) {
      console.error("Error fetching next code:", error);
    }
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setAutoGenerateCode(true);
    fetchNextCode();
  };

  // معالجة مسح الباركود للمخزون
  const handleInventoryBarcodeScan = (barcode) => {
    setFormData(prev => ({ ...prev, barcode: barcode }));
    setIsScannerOpen(false);
    toast.success(
      language === "ar" 
        ? `✅ تم مسح الباركود: ${barcode}` 
        : `✅ Barcode scanned: ${barcode}`
    );
  };

  // 🔊 تشغيل صوت عند المسح
  const playBeepSound = (success = true) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (success) {
        oscillator.frequency.value = 1200;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
      } else {
        oscillator.frequency.value = 300;
        oscillator.type = 'square';
        gainNode.gain.value = 0.2;
      }
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (e) {
      console.log("Audio not supported");
    }
  };

  // معالجة مسح الباركود لاستلام البضاعة
  const handleReceiveBarcodeScan = (barcode) => {
    const now = Date.now();
    if (now - lastScanTime < 500) return; // منع المسح المتكرر
    setLastScanTime(now);
    
    const product = products.find(p => 
      p.barcode === barcode || 
      p.code === barcode ||
      p.barcode?.toLowerCase() === barcode?.toLowerCase()
    );
    
    if (product) {
      playBeepSound(true);
      setReceiveProduct(product);
      setReceiveQuantity("1");
      setReceiveScannerOpen(false);
      toast.success(
        language === "ar" 
          ? `✅ تم العثور على: ${product.name}` 
          : `✅ Found: ${product.name}`
      );
    } else {
      playBeepSound(false);
      toast.error(
        language === "ar" 
          ? `❌ لم يتم العثور على منتج بهذا الباركود: ${barcode}` 
          : `❌ Product not found: ${barcode}`
      );
    }
  };

  // تأكيد استلام الكمية
  const handleConfirmReceive = async () => {
    if (!receiveProduct || !receiveQuantity || parseInt(receiveQuantity) <= 0) {
      toast.error(language === "ar" ? "أدخل كمية صحيحة" : "Enter valid quantity");
      return;
    }
    
    try {
      const newQuantity = (receiveProduct.quantity || 0) + parseInt(receiveQuantity);
      await axios.put(`${API}/products/${receiveProduct.id}`, {
        ...receiveProduct,
        quantity: newQuantity
      });
      
      // إضافة للتاريخ
      setReceiveHistory(prev => [{
        product: receiveProduct,
        quantity: parseInt(receiveQuantity),
        time: new Date().toLocaleTimeString(language === "ar" ? "ar-IQ" : "en-US"),
        newStock: newQuantity
      }, ...prev.slice(0, 19)]); // آخر 20 عملية
      
      toast.success(
        language === "ar" 
          ? `✅ تم إضافة ${receiveQuantity} ${receiveProduct.unit || 'قطعة'} من "${receiveProduct.name}" (المخزون الجديد: ${newQuantity})` 
          : `✅ Added ${receiveQuantity} to "${receiveProduct.name}" (New stock: ${newQuantity})`
      );
      
      fetchProducts();
      setReceiveProduct(null);
      setReceiveQuantity("");
      
      // إذا في وضع المسح المتواصل، افتح الماسح مرة أخرى
      if (scannerMode) {
        setReceiveScannerOpen(true);
      }
    } catch (error) {
      toast.error(language === "ar" ? "خطأ في تحديث المخزون" : "Error updating stock");
    }
  };

  // الاستماع لماسح USB في وضع استلام البضاعة
  useEffect(() => {
    if (!isReceiveMode) return;
    
    let buffer = "";
    let lastTime = Date.now();
    
    const handleKeyDown = (e) => {
      const currentTime = Date.now();
      const activeElement = document.activeElement;
      const isInputFocused = activeElement.tagName === "INPUT" || 
                            activeElement.tagName === "TEXTAREA";
      
      // تجاهل إذا في حقل الكمية
      if (isInputFocused && activeElement.type === "number") return;
      
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      
      // مسح المخزن المؤقت إذا مر وقت طويل
      if (currentTime - lastTime > 100) {
        buffer = "";
      }
      lastTime = currentTime;
      
      // عند Enter، معالجة الباركود
      if (e.key === "Enter" && buffer.length >= 3) {
        e.preventDefault();
        handleReceiveBarcodeScan(buffer.trim());
        buffer = "";
        return;
      }
      
      // إضافة الأحرف للمخزن المؤقت
      if (e.key.length === 1 && /[a-zA-Z0-9\-_.]/.test(e.key)) {
        if (!isInputFocused) {
          buffer += e.key;
        }
      }
    };
    
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isReceiveMode, products, scannerMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        code: autoGenerateCode && !editingProduct ? "" : formData.code,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        sale_price: parseFloat(formData.sale_price) || 0,
        quantity: parseInt(formData.quantity) || 0,
        min_stock: parseInt(formData.min_stock) || 5,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
        profit_margin: formData.profit_margin ? parseFloat(formData.profit_margin) : null,
        expiry_date: formData.expiry_date || null,
        large_unit: formData.large_unit || null,
        small_unit: formData.small_unit || null,
        units_per_package: formData.units_per_package ? parseFloat(formData.units_per_package) : null,
        large_unit_price: (formData.units_per_package && formData.sale_price)
          ? parseFloat(formData.sale_price) * parseFloat(formData.units_per_package)
          : null,
      };

      let productId;
      let isOfflinePending = false;
      
      if (editingProduct) {
        const upd = await axios.put(`${API}/products/${editingProduct.id}`, data);
        productId = editingProduct.id;
        isOfflinePending = upd?.data?._offline_pending === true;
        toast.success(
          isOfflinePending 
            ? (language === "ar" ? "💾 تم الحفظ محلياً — سيُرسل عند رجوع الإنترنت" : "💾 Saved locally — will sync online")
            : (language === "ar" ? "تم التحديث بنجاح" : "Updated successfully")
        );
      } else {
        const response = await axios.post(`${API}/products`, data);
        productId = response.data.id;
        isOfflinePending = response.data._offline_pending === true;
        toast.success(
          isOfflinePending 
            ? (language === "ar" ? "💾 تم الحفظ محلياً — سيُرسل عند رجوع الإنترنت" : "💾 Saved locally — will sync online")
            : (language === "ar" ? "تم الإضافة بنجاح" : "Added successfully")
        );
      }
      
      // رفع الصورة إذا تم اختيارها (يتطلب إنترنت — يُتجاوز offline)
      if (productImage && productId && !isOfflinePending) {
        await handleImageUpload(productId);
      } else if (productImage && isOfflinePending) {
        toast(language === "ar" ? "📷 الصورة لم تُرفع — التطبيق غير متصل بالإنترنت" : "📷 Image skipped — offline", {
          icon: "ℹ️",
        });
      }
      
      fetchProducts();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === "ar" ? "هل أنت متأكد من الحذف؟" : "Are you sure you want to delete?")) {
      return;
    }
    const product = products.find((p) => p.id === id);
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success(language === "ar" ? "تم الحذف بنجاح" : "Deleted successfully");
      // Audit log
      logAudit(token, {
        action: "product_delete",
        entity_type: "product",
        entity_id: id,
        entity_name: product?.name || id,
        description: `حذف منتج "${product?.name}" من المخزون`,
        employee_id: activeEmployee?.id,
        employee_name: activeEmployee?.name,
      });
      fetchProducts();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setAutoGenerateCode(false);
    setFormData({
      code: product.code || "",
      name: product.name || "",
      purchase_price: product.purchase_price?.toString() || "",
      sale_price: product.sale_price?.toString() || "",
      quantity: product.quantity?.toString() || "",
      category: product.category || "",
      min_stock: product.min_stock?.toString() || "5",
      unit: product.unit || "",
      barcode: product.barcode || "",
      supplier_name: product.supplier_name || "",
      notes: product.notes || "",
      location: product.location || "",
      brand: product.brand || "",
      weight: product.weight || "",
      variant: product.variant || "",
      cost_price: product.cost_price?.toString() || "",
      profit_margin: product.profit_margin?.toString() || "",
      expiry_date: product.expiry_date || "",
      large_unit: product.large_unit || "",
      small_unit: product.small_unit || "",
      units_per_package: product.units_per_package?.toString() || "",
    });
    // عرض صورة المنتج إن وجدت
    setImagePreview(product.image_url || null);
    setProductImage(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
    setFormData(initialFormData);
    setAutoGenerateCode(true);
    setActiveTab("basic");
    setProductImage(null);
    setImagePreview(null);
  };

  // رفع صورة المنتج
  const handleImageUpload = async (productId) => {
    if (!productImage) return null;
    
    setUploadingImage(true);
    try {
      const formDataImg = new FormData();
      formDataImg.append('file', productImage);
      
      const response = await axios.post(`${API}/products/${productId}/image`, formDataImg, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(language === "ar" ? "تم رفع الصورة بنجاح" : "Image uploaded successfully");
      return response.data.image_url;
    } catch (error) {
      toast.error(language === "ar" ? "فشل رفع الصورة" : "Failed to upload image");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // حذف صورة المنتج
  const handleDeleteImage = async (productId) => {
    if (!productId) {
      setImagePreview(null);
      setProductImage(null);
      return;
    }
    
    try {
      await axios.delete(`${API}/products/${productId}/image`);
      setImagePreview(null);
      toast.success(language === "ar" ? "تم حذف الصورة" : "Image deleted");
      fetchProducts();
    } catch (error) {
      toast.error(language === "ar" ? "فشل حذف الصورة" : "Failed to delete image");
    }
  };

  // معالجة اختيار الصورة
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(language === "ar" ? "يجب أن يكون الملف صورة" : "File must be an image");
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        toast.error(language === "ar" ? "حجم الصورة كبير جداً (الحد الأقصى 5MB)" : "Image too large (max 5MB)");
        return;
      }
      setProductImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const toggleRowExpand = (productId) => {
    setExpandedRows(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.barcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // حساب هامش الربح تلقائياً
  const calculateProfitMargin = () => {
    const purchase = parseFloat(formData.purchase_price);
    const sale = parseFloat(formData.sale_price);
    if (purchase > 0 && sale > 0) {
      const margin = ((sale - purchase) / purchase * 100).toFixed(2);
      setFormData(prev => ({ ...prev, profit_margin: margin }));
    }
  };

  // حساب قيم المخزون الإجمالية
  const inventoryStats = {
    totalPurchaseValue: products.reduce((sum, p) => sum + (p.purchase_price * p.quantity), 0),
    totalSaleValue: products.reduce((sum, p) => sum + (p.sale_price * p.quantity), 0),
    totalQuantity: products.reduce((sum, p) => sum + p.quantity, 0),
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">
            {t("inventory")}
          </h1>
          <p className="text-slate-400 mt-1">
            {language === "ar"
              ? `إجمالي المنتجات: ${products.length}`
              : `Total Products: ${products.length}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* ✨ زر تصحيح المخزون - يصلح الفواتير القديمة اللي ما خصمت */}
          <Button
            onClick={async () => {
              try {
                const preview = await axios.get(`${API}/inventory-fix/preview`);
                const p = preview.data;
                if (p.total_invoices === 0) {
                  toast.success(language === "ar" ? "✅ المخزون متطابق - لا توجد فواتير تحتاج تصحيح" : "Inventory is in sync");
                  return;
                }
                if (!window.confirm(
                  language === "ar"
                    ? `⚠️ تنبيه مهم!\n\nسيتم تصحيح ${p.purchases_count} فاتورة شراء و ${p.sales_count} فاتورة بيع.\n\nهذا سيؤدي إلى:\n• إضافة ${p.purchases_items_count} عنصر للمخزون\n• خصم ${p.sales_items_count} عنصر من المخزون\n• إنشاء المنتجات المفقودة تلقائياً\n\nهل تريد المتابعة؟`
                    : `${p.total_invoices} invoices will be reconciled. Continue?`
                )) return;
                
                const r = await axios.post(`${API}/inventory-fix/apply`);
                const s = r.data.stats;
                toast.success(
                  language === "ar"
                    ? `✅ تم التصحيح!\n• ${s.purchases_processed} مشتريات\n• ${s.sales_processed} مبيعات\n• ${s.products_auto_created} منتج جديد`
                    : `Reconciled: ${s.purchases_processed} purchases, ${s.sales_processed} sales`,
                  { duration: 8000 }
                );
                fetchProducts();
              } catch (e) {
                toast.error(e.response?.data?.detail || e.message);
              }
            }}
            variant="outline"
            className="border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white"
            data-testid="inventory-fix-btn"
            title={language === "ar" ? "تصحيح المخزون من الفواتير القديمة" : "Fix inventory from old invoices"}
          >
            🔧 {language === "ar" ? "تصحيح المخزون" : "Fix Inventory"}
          </Button>
          
          {/* زر استلام البضاعة */}
          <Button
            onClick={() => setIsReceiveMode(!isReceiveMode)}
            className={`transition-all ${
              isReceiveMode 
                ? "bg-amber-500 hover:bg-amber-600 ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900" 
                : "bg-amber-600 hover:bg-amber-700"
            }`}
            data-testid="receive-goods-btn"
          >
            <Truck className={`w-4 h-4 me-2 ${isReceiveMode ? "animate-pulse" : ""}`} />
            {isReceiveMode 
              ? (language === "ar" ? "إنهاء الاستلام" : "End Receiving")
              : (language === "ar" ? "استلام بضاعة" : "Receive Goods")
            }
          </Button>
          <Button
            onClick={() => {
              try {
                exportInventoryToExcel(products, language);
                toast.success(language === "ar" ? "تم تصدير المخزون" : "Inventory exported");
              } catch (e) {
                toast.error(e.message);
              }
            }}
            variant="outline"
            className="border-emerald-600 text-emerald-500 hover:bg-emerald-600 hover:text-white"
            data-testid="export-inventory-btn"
          >
            <FileSpreadsheet className="w-4 h-4 me-2" />
            Excel
          </Button>
          {/* زر تقرير الجرد */}
          <Button
            onClick={() => setShowInventoryReport(!showInventoryReport)}
            variant="outline"
            className={`border-purple-600 text-purple-500 hover:bg-purple-600 hover:text-white ${showInventoryReport ? 'bg-purple-600 text-white' : ''}`}
            data-testid="inventory-report-btn"
          >
            <ClipboardList className="w-4 h-4 me-2" />
            {language === "ar" ? "تقرير جرد" : "Stock Report"}
          </Button>
          <Button
            onClick={handleOpenDialog}
            className="bg-emerald-600 hover:bg-emerald-700 btn-lift"
            data-testid="add-product-btn"
          >
            <Plus className={`w-4 h-4 ${language === "ar" ? "ml-2" : "mr-2"}`} />
            {t("addProduct")}
          </Button>
        </div>
      </div>

      {/* 📋 تقرير جرد المخزون */}
      {showInventoryReport && (
        <Card className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border-purple-500/50 print:bg-white print:border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 print:mb-6">
              <h3 className="text-xl font-bold text-purple-400 flex items-center gap-2 print:text-black">
                <ClipboardList className="w-6 h-6" />
                {language === "ar" ? "تقرير جرد المخزون" : "Inventory Stock Report"}
              </h3>
              <div className="flex gap-2 print:hidden">
                <Button
                  onClick={() => window.print()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Printer className="w-4 h-4 me-2" />
                  {language === "ar" ? "طباعة" : "Print"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowInventoryReport(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* معلومات التقرير */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 print:grid-cols-4">
              <div className="p-3 rounded-lg bg-slate-800/50 print:bg-gray-100">
                <p className="text-slate-400 text-xs print:text-gray-600">{language === "ar" ? "تاريخ التقرير" : "Report Date"}</p>
                <p className="text-white font-bold print:text-black">{new Date().toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US")}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 print:bg-gray-100">
                <p className="text-slate-400 text-xs print:text-gray-600">{language === "ar" ? "عدد المنتجات" : "Total Products"}</p>
                <p className="text-white font-bold print:text-black">{products.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 print:bg-gray-100">
                <p className="text-slate-400 text-xs print:text-gray-600">{language === "ar" ? "إجمالي القطع" : "Total Items"}</p>
                <p className="text-white font-bold print:text-black">{products.reduce((sum, p) => sum + (p.quantity || 0), 0).toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50 print:bg-gray-100">
                <p className="text-slate-400 text-xs print:text-gray-600">{language === "ar" ? "قيمة المخزون" : "Stock Value"}</p>
                <p className="text-emerald-400 font-bold print:text-green-600">{products.reduce((sum, p) => sum + ((p.quantity || 0) * (p.sale_price || 0)), 0).toLocaleString()} {language === "ar" ? "د.ع" : "IQD"}</p>
              </div>
            </div>
            
            {/* جدول الجرد */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 print:border-gray-300">
                    <th className="text-start py-2 text-purple-400 print:text-gray-700">#</th>
                    <th className="text-start py-2 text-purple-400 print:text-gray-700">{language === "ar" ? "الكود" : "Code"}</th>
                    <th className="text-start py-2 text-purple-400 print:text-gray-700">{language === "ar" ? "المنتج" : "Product"}</th>
                    <th className="text-center py-2 text-purple-400 print:text-gray-700">{language === "ar" ? "الكمية" : "Qty"}</th>
                    <th className="text-center py-2 text-purple-400 print:text-gray-700">{language === "ar" ? "سعر الشراء" : "Cost"}</th>
                    <th className="text-center py-2 text-purple-400 print:text-gray-700">{language === "ar" ? "سعر البيع" : "Price"}</th>
                    <th className="text-center py-2 text-purple-400 print:text-gray-700">{language === "ar" ? "القيمة" : "Value"}</th>
                    <th className="text-center py-2 text-purple-400 print:text-gray-700 print:hidden">{language === "ar" ? "الحالة" : "Status"}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, idx) => (
                    <tr key={product.id} className="border-b border-slate-800 print:border-gray-200 hover:bg-slate-800/30">
                      <td className="py-2 text-slate-400 print:text-gray-600">{idx + 1}</td>
                      <td className="py-2 text-slate-300 font-mono print:text-gray-800">{product.code}</td>
                      <td className="py-2 text-white print:text-black">{product.name}</td>
                      <td className="py-2 text-center">
                        <span className={`font-bold ${
                          (product.quantity || 0) < 0
                            ? 'text-rose-500 print:text-red-700'
                            : (product.quantity || 0) <= (product.minimum_stock || 5) 
                              ? 'text-red-400 print:text-red-600' 
                              : 'text-white print:text-black'
                        }`}>
                          {product.quantity || 0}
                        </span>
                      </td>
                      <td className="py-2 text-center text-slate-300 print:text-gray-700">{(product.purchase_price || 0).toLocaleString()}</td>
                      <td className="py-2 text-center text-slate-300 print:text-gray-700">{(product.sale_price || 0).toLocaleString()}</td>
                      <td className="py-2 text-center text-emerald-400 font-bold print:text-green-600">
                        {((product.quantity || 0) * (product.sale_price || 0)).toLocaleString()}
                      </td>
                      <td className="py-2 text-center print:hidden">
                        {(product.quantity || 0) < 0 ? (
                          <span className="px-2 py-1 rounded text-xs bg-rose-600/30 text-rose-300 border border-rose-500/50 animate-pulse">
                            {language === "ar" ? "⚠️ سالب" : "Negative"}
                          </span>
                        ) : (product.quantity || 0) === 0 ? (
                          <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">{language === "ar" ? "نفذ" : "Out"}</span>
                        ) : (product.quantity || 0) <= (product.minimum_stock || 5) ? (
                          <span className="px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-400">{language === "ar" ? "منخفض" : "Low"}</span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-400">{language === "ar" ? "متوفر" : "OK"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-purple-500 print:border-gray-400">
                    <td colSpan="3" className="py-3 text-purple-400 font-bold print:text-gray-700">{language === "ar" ? "الإجمالي" : "Total"}</td>
                    <td className="py-3 text-center text-white font-bold print:text-black">{products.reduce((sum, p) => sum + (p.quantity || 0), 0).toLocaleString()}</td>
                    <td colSpan="2" className="py-3"></td>
                    <td className="py-3 text-center text-emerald-400 font-bold print:text-green-600">
                      {products.reduce((sum, p) => sum + ((p.quantity || 0) * (p.sale_price || 0)), 0).toLocaleString()} {language === "ar" ? "د.ع" : "IQD"}
                    </td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            {/* ملاحظات */}
            <div className="mt-4 p-3 rounded-lg bg-slate-800/50 print:bg-gray-100 print:mt-6">
              <p className="text-slate-400 text-xs print:text-gray-600">{language === "ar" ? "ملاحظات:" : "Notes:"}</p>
              <div className="grid grid-cols-3 gap-4 mt-2 text-xs">
                <p className="text-red-400 print:text-red-600">● {language === "ar" ? "منتجات نفذت:" : "Out of stock:"} {products.filter(p => (p.quantity || 0) === 0).length}</p>
                <p className="text-amber-400 print:text-amber-600">● {language === "ar" ? "كمية منخفضة:" : "Low stock:"} {products.filter(p => (p.quantity || 0) > 0 && (p.quantity || 0) <= (p.minimum_stock || 5)).length}</p>
                <p className="text-emerald-400 print:text-green-600">● {language === "ar" ? "متوفر:" : "In stock:"} {products.filter(p => (p.quantity || 0) > (p.minimum_stock || 5)).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 📦 قسم استلام البضاعة */}
      {isReceiveMode && (
        <Card className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-500/50">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* العمود الأيسر - المسح والإدخال */}
              <div className="flex-1 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-amber-400 flex items-center gap-2">
                    <Truck className="w-6 h-6" />
                    {language === "ar" ? "استلام بضاعة من المورد" : "Receive Goods from Supplier"}
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsReceiveMode(false);
                      setReceiveProduct(null);
                      setReceiveHistory([]);
                    }}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* أزرار المسح */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => setReceiveScannerOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="receive-camera-scan-btn"
                  >
                    <Camera className="w-5 h-5 me-2" />
                    {language === "ar" ? "مسح بالكاميرا" : "Camera Scan"}
                  </Button>
                  <Button
                    onClick={() => setScannerMode(!scannerMode)}
                    className={`transition-all ${
                      scannerMode 
                        ? "bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-400 animate-pulse" 
                        : "bg-slate-700 hover:bg-slate-600"
                    }`}
                    data-testid="receive-usb-mode-btn"
                  >
                    <ScanLine className="w-5 h-5 me-2" />
                    {scannerMode 
                      ? (language === "ar" ? "🟢 جاهز للمسح USB" : "🟢 USB Ready")
                      : (language === "ar" ? "وضع USB" : "USB Mode")
                    }
                  </Button>
                </div>
                
                {/* تلميح */}
                <div className={`text-sm p-3 rounded-lg ${
                  scannerMode 
                    ? "bg-emerald-500/20 border border-emerald-500/50 text-emerald-400" 
                    : "bg-slate-800/50 text-slate-400"
                }`}>
                  {scannerMode ? (
                    <span>🟢 {language === "ar" ? "وضع المسح USB نشط - امسح الباركود الآن!" : "USB scan mode active - scan barcode now!"}</span>
                  ) : (
                    <span>💡 {language === "ar" ? "امسح الباركود أو استخدم البحث أدناه" : "Scan barcode or use search below"}</span>
                  )}
                </div>
                
                {/* البحث اليدوي */}
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder={language === "ar" ? "ابحث عن المنتج بالاسم أو الكود..." : "Search product by name or code..."}
                    className="pr-10 bg-slate-900 border-slate-700 text-white"
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase();
                      if (value.length >= 2) {
                        const found = products.find(p => 
                          p.name?.toLowerCase().includes(value) ||
                          p.code?.toLowerCase().includes(value) ||
                          p.barcode?.toLowerCase() === value
                        );
                        if (found && e.target.value.length >= 3) {
                          // لا نفعل شيء هنا، فقط نعرض الاقتراحات
                        }
                      }
                    }}
                    data-testid="receive-search-input"
                  />
                </div>
                
                {/* قائمة المنتجات للاختيار السريع */}
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {products.slice(0, 10).map(product => (
                    <button
                      key={product.id}
                      onClick={() => {
                        playBeepSound(true);
                        setReceiveProduct(product);
                        setReceiveQuantity("1");
                      }}
                      className="w-full text-start p-2 rounded bg-slate-800/50 hover:bg-slate-700 text-sm flex items-center justify-between"
                    >
                      <span className="text-white">{product.name}</span>
                      <span className="text-slate-400 text-xs">{product.code} | مخزون: {product.quantity}</span>
                    </button>
                  ))}
                </div>
                
                {/* المنتج المحدد */}
                {receiveProduct && (
                  <div className="p-4 rounded-lg bg-emerald-500/20 border border-emerald-500/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-400 font-bold text-lg">{receiveProduct.name}</p>
                        <p className="text-slate-400 text-sm">
                          {language === "ar" ? "الكود:" : "Code:"} {receiveProduct.code} | 
                          {language === "ar" ? " المخزون الحالي:" : " Current Stock:"} {receiveProduct.quantity}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setReceiveProduct(null)}
                        className="text-slate-400"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-slate-300 text-sm">
                          {language === "ar" ? "الكمية المستلمة" : "Quantity Received"}
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          value={receiveQuantity}
                          onChange={(e) => setReceiveQuantity(e.target.value)}
                          className="bg-slate-900 border-slate-700 text-white text-xl font-bold text-center"
                          placeholder="0"
                          autoFocus
                          data-testid="receive-quantity-input"
                        />
                      </div>
                      <Button
                        onClick={handleConfirmReceive}
                        className="bg-emerald-600 hover:bg-emerald-700 h-16 px-6"
                        data-testid="confirm-receive-btn"
                      >
                        <Check className="w-6 h-6 me-2" />
                        {language === "ar" ? "تأكيد" : "Confirm"}
                      </Button>
                    </div>
                    
                    <p className="text-center text-slate-400 text-sm">
                      {language === "ar" 
                        ? `المخزون الجديد سيصبح: ${(receiveProduct.quantity || 0) + (parseInt(receiveQuantity) || 0)}`
                        : `New stock will be: ${(receiveProduct.quantity || 0) + (parseInt(receiveQuantity) || 0)}`
                      }
                    </p>
                  </div>
                )}
              </div>
              
              {/* العمود الأيمن - تاريخ الاستلام */}
              <div className="lg:w-80 space-y-3">
                <h4 className="text-slate-300 font-medium flex items-center gap-2">
                  <History className="w-4 h-4" />
                  {language === "ar" ? "سجل الاستلام (هذه الجلسة)" : "Receive Log (this session)"}
                </h4>
                
                {receiveHistory.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">
                    {language === "ar" ? "لا توجد عمليات استلام بعد" : "No receives yet"}
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {receiveHistory.map((item, idx) => (
                      <div key={idx} className="p-2 rounded bg-slate-800/50 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-emerald-400 font-medium">+{item.quantity}</span>
                          <span className="text-slate-500 text-xs">{item.time}</span>
                        </div>
                        <p className="text-white truncate">{item.product.name}</p>
                        <p className="text-slate-400 text-xs">
                          {language === "ar" ? "المخزون الجديد:" : "New stock:"} {item.newStock}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {receiveHistory.length > 0 && (
                  <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-center">
                    <p className="text-emerald-400 font-bold">
                      {language === "ar" ? "إجمالي المستلم:" : "Total Received:"} {" "}
                      {receiveHistory.reduce((sum, item) => sum + item.quantity, 0)} {language === "ar" ? "قطعة" : "items"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ماسح الباركود لاستلام البضاعة */}
      {receiveScannerOpen && (
        <BarcodeScanner
          onScan={handleReceiveBarcodeScan}
          onClose={() => setReceiveScannerOpen(false)}
          language={language}
        />
      )}

      {/* Inventory Summary Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-${canViewPurchasePrice ? '3' : '1'} gap-4`}>
        {canViewPurchasePrice && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <p className="text-slate-400 text-sm">{language === "ar" ? "قيمة الشراء" : "Purchase Value"}</p>
              <p className="text-2xl font-bold text-amber-500">{formatCurrency(inventoryStats.totalPurchaseValue)}</p>
              <USDHint amount={inventoryStats.totalPurchaseValue} />
            </CardContent>
          </Card>
        )}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">{language === "ar" ? "قيمة البيع" : "Sale Value"}</p>
            <p className="text-2xl font-bold text-green-500">{formatCurrency(inventoryStats.totalSaleValue)}</p>
            <USDHint amount={inventoryStats.totalSaleValue} />
          </CardContent>
        </Card>
        {canViewPurchasePrice && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <p className="text-slate-400 text-sm">{language === "ar" ? "الربح المتوقع" : "Expected Profit"}</p>
              <p className="text-2xl font-bold text-blue-500">{formatCurrency(inventoryStats.totalSaleValue - inventoryStats.totalPurchaseValue)}</p>
              <USDHint amount={inventoryStats.totalSaleValue - inventoryStats.totalPurchaseValue} />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Search */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400`} />
            <Input
              placeholder={language === "ar" ? "بحث بالاسم، الكود، الباركود، أو الماركة..." : "Search by name, code, barcode, or brand..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${language === "ar" ? "pr-10" : "pl-10"} bg-slate-900 border-slate-700 text-white`}
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{t("noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500 w-10"></TableHead>
                    <TableHead className="text-amber-500">{labels.code}</TableHead>
                    <TableHead className="text-amber-500">{labels.name}</TableHead>
                    <TableHead className="text-amber-500">{labels.brand}</TableHead>
                    {canViewPurchasePrice && (
                      <TableHead className="text-amber-500">{labels.purchase_price}</TableHead>
                    )}
                    <TableHead className="text-amber-500">{labels.sale_price}</TableHead>
                    <TableHead className="text-amber-500">{labels.quantity}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "القيمة" : "Value"}</TableHead>
                    <TableHead className="text-amber-500">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <React.Fragment key={product.id}>
                      <TableRow
                        className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                        data-testid={`product-row-${product.id}`}
                        onClick={() => toggleRowExpand(product.id)}
                      >
                        <TableCell className="w-10">
                          {expandedRows[product.id] ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-slate-300">
                          {product.code}
                        </TableCell>
                        <TableCell className="text-white font-medium">
                          {product.name}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {product.brand || "-"}
                        </TableCell>
                        {canViewPurchasePrice && (
                          <TableCell className="text-slate-300">
                            {formatCurrency(product.purchase_price)}
                          </TableCell>
                        )}
                        <TableCell className="text-green-500 font-medium">
                          {formatCurrency(product.sale_price)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`flex items-center gap-1 ${
                              product.quantity <= product.min_stock
                                ? "text-red-500"
                                : "text-slate-300"
                            }`}
                          >
                            {product.quantity <= product.min_stock && (
                              <AlertTriangle className="w-4 h-4" />
                            )}
                            {product.quantity}
                          </span>
                        </TableCell>
                        <TableCell className="text-amber-500 font-medium">
                          {formatCurrency(product.sale_price * product.quantity)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(product)}
                              className="text-slate-400 hover:text-amber-500"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(product.id)}
                              className="text-slate-400 hover:text-red-500"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded Row Details */}
                      {expandedRows[product.id] && (
                        <TableRow className="bg-slate-900/50 border-slate-700">
                          <TableCell colSpan={10} className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
                              {/* Barcode Display */}
                              {product.barcode && (
                                <div className="col-span-2 md:col-span-2 lg:col-span-2">
                                  <span className="text-slate-500 block mb-2">{labels.barcode}:</span>
                                  <ProductBarcode 
                                    barcode={product.barcode} 
                                    productName={product.name}
                                  />
                                </div>
                              )}
                              {!product.barcode && (
                                <div>
                                  <span className="text-slate-500">{labels.barcode}:</span>
                                  <p className="text-white font-mono">-</p>
                                </div>
                              )}
                              <div>
                                <span className="text-slate-500">{labels.supplier_name}:</span>
                                <p className="text-white">{product.supplier_name || "-"}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">{labels.location}:</span>
                                <p className="text-white">{product.location || "-"}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">{labels.weight}:</span>
                                <p className="text-white">{product.weight || "-"}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">{labels.variant}:</span>
                                <p className="text-white">{product.variant || "-"}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">{labels.min_stock}:</span>
                                <p className="text-white">{product.min_stock}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">{labels.profit_margin}:</span>
                                <p className="text-green-400">{product.profit_margin ? `${product.profit_margin}%` : "-"}</p>
                              </div>
                              {/* تاريخ الانتهاء */}
                              <div>
                                <span className="text-slate-500 flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-red-400" />
                                  {labels.expiry_date}:
                                </span>
                                <p className={`${
                                  product.expiry_date && new Date(product.expiry_date) < new Date()
                                    ? 'text-red-400'
                                    : product.expiry_date && new Date(product.expiry_date) < new Date(Date.now() + 30*24*60*60*1000)
                                    ? 'text-amber-400'
                                    : 'text-white'
                                }`}>
                                  {product.expiry_date 
                                    ? new Date(product.expiry_date).toLocaleDateString('ar-IQ')
                                    : "-"}
                                  {product.expiry_date && new Date(product.expiry_date) < new Date() && (
                                    <span className="text-red-400 text-xs ms-1">(منتهي)</span>
                                  )}
                                </p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-slate-500">{labels.notes}:</span>
                                <p className="text-white">{product.notes || "-"}</p>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingProduct ? t("editProduct") : t("addProduct")}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
              {/* القسم الأول: المعلومات الأساسية */}
              <div className="space-y-4">
                {/* Auto Generate Code Toggle */}
                {!editingProduct && (
                  <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    <input
                      type="checkbox"
                      id="autoGenerateCode"
                      checked={autoGenerateCode}
                      onChange={(e) => {
                        setAutoGenerateCode(e.target.checked);
                        if (e.target.checked) {
                          fetchNextCode();
                        }
                      }}
                      className="w-4 h-4 accent-red-600"
                    />
                    <label htmlFor="autoGenerateCode" className="text-slate-200 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" />
                      {labels.autoGenerate}
                    </label>
                    {autoGenerateCode && nextCode && (
                      <span className="text-amber-500 font-mono text-sm">
                        → {nextCode}
                      </span>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.code}</Label>
                    <Input
                      value={autoGenerateCode && !editingProduct ? nextCode : formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white font-mono"
                      disabled={autoGenerateCode && !editingProduct}
                      placeholder={autoGenerateCode ? labels.codeWillBeGenerated : ""}
                      data-testid="product-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.name} *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.category}</Label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      data-testid="category-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.brand}</Label>
                    <Input
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      data-testid="brand-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200 flex items-center gap-2">
                      <Barcode className="w-4 h-4" />
                      {labels.barcode}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white font-mono flex-1"
                        data-testid="barcode-input"
                        placeholder={language === "ar" ? "امسح أو أدخل الباركود" : "Scan or enter barcode"}
                      />
                      <Button
                        type="button"
                        onClick={() => setIsScannerOpen(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 px-3"
                        title={language === "ar" ? "مسح الباركود بالكاميرا" : "Scan barcode with camera"}
                        data-testid="scan-barcode-btn"
                      >
                        <Camera className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.unit}</Label>
                    <Select
                      value={formData.unit && !Object.keys(labels.units).includes(formData.unit) ? "other" : formData.unit}
                      onValueChange={(value) => {
                        if (value === "other") {
                          setFormData({ ...formData, unit: "" });
                        } else {
                          setFormData({ ...formData, unit: value });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="unit-select">
                        <SelectValue placeholder={language === "ar" ? "اختر الوحدة" : "Select unit"} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {Object.entries(labels.units).map(([key, value]) => (
                          <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">
                            {value}
                          </SelectItem>
                        ))}
                        <SelectItem value="other" className="text-amber-400 hover:bg-slate-700">
                          {language === "ar" ? "أخرى (كتابة يدوية)" : "Other (custom)"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {(formData.unit === "" || (!Object.keys(labels.units).includes(formData.unit) && formData.unit !== "")) && (
                      <Input
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                        placeholder={language === "ar" ? "اكتب الوحدة هنا..." : "Type unit here..."}
                        className="bg-slate-900 border-slate-700 text-white mt-2"
                      />
                    )}
                  </div>
                </div>
                
                {/* نظام الوحدات المزدوج */}
                <div className="mt-4 p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-cyan-300 text-sm font-semibold">
                      📦 {language === "ar" ? "نظام الوحدات المزدوج (اختياري)" : "Dual Unit System (optional)"}
                    </Label>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    {language === "ar"
                      ? "مثال: كرتون (وحدة كبيرة) = 24 حبة (وحدة صغيرة). مفيد لبيع بالجملة والمفرد."
                      : "Example: Carton (large) = 24 pieces (small). Useful for wholesale & retail."}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-slate-300 text-xs">الوحدة الكبيرة</Label>
                      <Input
                        value={formData.large_unit}
                        onChange={(e) => setFormData({ ...formData, large_unit: e.target.value })}
                        placeholder="كرتون / طن"
                        className="bg-slate-900 border-slate-700 text-white text-sm"
                        data-testid="large-unit-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-300 text-xs">الوحدة الصغيرة</Label>
                      <Input
                        value={formData.small_unit}
                        onChange={(e) => setFormData({ ...formData, small_unit: e.target.value })}
                        placeholder="حبة / كيس"
                        className="bg-slate-900 border-slate-700 text-white text-sm"
                        data-testid="small-unit-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-300 text-xs">عدد الصغيرة في الكبيرة</Label>
                      <Input
                        type="number"
                        value={formData.units_per_package}
                        onChange={(e) => setFormData({ ...formData, units_per_package: e.target.value })}
                        placeholder="24"
                        className="bg-slate-900 border-slate-700 text-white text-sm"
                        data-testid="units-per-package-input"
                      />
                    </div>
                  </div>
                  {formData.units_per_package && formData.sale_price && (
                    <div className="mt-2 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                      💰 سعر {formData.large_unit || "الكبيرة"}: {(parseFloat(formData.sale_price) * parseFloat(formData.units_per_package)).toLocaleString("ar-IQ")} د.ع
                    </div>
                  )}
                </div>
              </div>

              {/* القسم الثاني: الأسعار والكميات */}
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <h4 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {language === "ar" ? "الأسعار والكميات" : "Prices & Quantities"}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.purchase_price} *</Label>
                    <PriceInput
                      value={parseFloat(formData.purchase_price) || 0}
                      onChange={(v) => {
                        setFormData({ ...formData, purchase_price: String(v) });
                      }}
                      placeholder="0"
                      testId="purchase-price-input"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.sale_price} *</Label>
                    <PriceInput
                      value={parseFloat(formData.sale_price) || 0}
                      onChange={(v) => {
                        setFormData({ ...formData, sale_price: String(v) });
                      }}
                      placeholder="0"
                      testId="sale-price-input"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.quantity} *</Label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      required
                      placeholder="0"
                      data-testid="quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.min_stock}</Label>
                    <Input
                      type="number"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder="5"
                      data-testid="min-stock-input"
                    />
                  </div>
                </div>
                {formData.profit_margin && (
                  <p className="text-green-400 text-sm mt-3 font-medium">
                    {language === "ar" ? `✓ هامش الربح: ${formData.profit_margin}%` : `✓ Profit Margin: ${formData.profit_margin}%`}
                  </p>
                )}
              </div>

              {/* القسم الثالث: تاريخ الانتهاء والمورد */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <Label className="text-red-400 font-medium flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4" />
                    {labels.expiry_date}
                  </Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="expiry-date-input"
                  />
                  {formData.expiry_date && new Date(formData.expiry_date) < new Date() && (
                    <p className="text-red-400 text-xs flex items-center gap-1 mt-2">
                      <AlertTriangle className="w-3 h-3" />
                      {language === "ar" ? "⚠️ منتهي الصلاحية!" : "⚠️ Expired!"}
                    </p>
                  )}
                  {formData.expiry_date && new Date(formData.expiry_date) > new Date() && 
                   new Date(formData.expiry_date) < new Date(Date.now() + 30*24*60*60*1000) && (
                    <p className="text-amber-400 text-xs flex items-center gap-1 mt-2">
                      <AlertTriangle className="w-3 h-3" />
                      {language === "ar" ? "⚠️ قريب من الانتهاء!" : "⚠️ Expiring soon!"}
                    </p>
                  )}
                </div>
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <Label className="text-blue-400 font-medium mb-2 block">{labels.supplier_name}</Label>
                  <Select
                    value={suppliers.some(s => s.name === formData.supplier_name) ? formData.supplier_name : ""}
                    onValueChange={(value) => setFormData({ ...formData, supplier_name: value })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="supplier-select">
                      <SelectValue placeholder={language === "ar" ? "اختر من القائمة" : "Select supplier"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.name} className="text-white hover:bg-slate-700">
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={language === "ar" ? "أو اكتب اسم المورد" : "Or type supplier name"}
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white mt-2"
                    data-testid="supplier-manual-input"
                  />
                </div>
              </div>

              {/* القسم الرابع: صورة المنتج */}
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <h4 className="text-purple-400 font-medium mb-3 flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  {language === "ar" ? "صورة المنتج" : "Product Image"}
                </h4>
                <div className="flex items-start gap-4">
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Product" 
                          className="w-20 h-20 object-cover rounded-lg border-2 border-purple-500/50"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(editingProduct?.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-slate-800 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center">
                        <Image className="w-6 h-6 text-slate-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                      id="product-image-input"
                      data-testid="product-image-input"
                    />
                    <label
                      htmlFor="product-image-input"
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg cursor-pointer transition-colors text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      {language === "ar" ? "اختر صورة" : "Choose Image"}
                    </label>
                    {uploadingImage && (
                      <p className="text-purple-400 text-sm mt-1 animate-pulse">
                        {language === "ar" ? "جاري الرفع..." : "Uploading..."}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* القسم الخامس: معلومات إضافية (اختياري) */}
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <h4 className="text-slate-300 font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {language === "ar" ? "معلومات إضافية (اختياري)" : "Additional Info (Optional)"}
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {labels.location}
                    </Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder={language === "ar" ? "رف A3" : "Shelf A3"}
                      data-testid="location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.weight}</Label>
                    <Input
                      value={formData.weight}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder={language === "ar" ? "500 جرام" : "500g"}
                      data-testid="weight-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">{labels.variant}</Label>
                    <Input
                      value={formData.variant}
                      onChange={(e) => setFormData({ ...formData, variant: e.target.value })}
                      className="bg-slate-900 border-slate-700 text-white"
                      placeholder={language === "ar" ? "أحمر / كبير" : "Red / Large"}
                      data-testid="variant-input"
                    />
                  </div>
                </div>
                <div className="space-y-2 mt-4">
                  <Label className="text-slate-200">{labels.notes}</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-700 text-white rounded-md p-3 min-h-[60px] resize-none text-sm"
                    placeholder={language === "ar" ? "أضف ملاحظات..." : "Add notes..."}
                    data-testid="notes-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700"
                data-testid="save-product-btn"
              >
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ماسح الباركود للمخزون */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScan={handleInventoryBarcodeScan}
        language={language}
      />
    </div>
  );
}
